// useSessionImageJobs — listens to the backend's per-training session-job socket
// events and surfaces COMPLETED image-generation jobs to the chat so they can be
// added to the current scene.
//
// This is the chat window's OWN, independent Socket.IO connection. The async
// Activity widget (aidea.asyncwidget, embedded via JobWidgetHost) keeps its own
// separate socket + behaviour; two clients to the same `trainingId` room is fine.
// We deliberately mirror the widget's event reading (see aidea.asyncwidget/src/
// App.tsx ~lines 209-300) so we stay wire-compatible without touching it:
//   - connect to the backend root (rootUrl minus a trailing `/api`),
//     `auth: bearerSocketAuth(getToken)` ("Bearer <jwt>", re-read per reconnect)
//   - on connect, `emit('join_session', { trainingId })`
//   - listen for `session_job_update` (progress) and `session_job_completed`
//   - read the job id from `jobId || id`, the type from `jobType || type`, and
//     the image URL from result/metadata (same field order the widget reads).
//
// The trainingId is the editor's `?id=` URL param (the room the backend keys on).
// If there's no trainingId or no token we no-op gracefully (returns []), so the
// chat works unchanged when opened standalone.

import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";

import { getToken } from "../../../common/environment_config";
import { bearerSocketAuth } from "../../utils/socketAuth";
import { onJobsSettled } from "../../utils/jobSettled";

// Read the editor's training id from the `?id=` URL param (the socket room key).
function readTrainingId() {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

// Backend Socket.IO root: `window.ideaLibraryRootUrl` with any trailing `/api`
// (and trailing slashes) stripped — the socket server runs at the root, while
// REST lives under `/api`. Mirrors the widget's `backendUrl.replace(/\/api\/?$/, "")`.
function readSocketRoot() {
  if (typeof window === "undefined") return null;
  const root = window.ideaLibraryRootUrl;
  if (typeof root !== "string" || !root) return null;
  return root.replace(/\/+$/, "").replace(/\/api$/, "");
}

// Does this job look like an image-generation job? Mirror the widget's job-type
// vocabulary (text-to-image / image-generation / preview-image / image-edit …).
// We match loosely on the type string containing "image" but exclude the
// image-to-* avatar/video families, which produce videos, not still images.
function isImageJob(jobType) {
  const t = String(jobType || "").toLowerCase();
  if (!t) return false;
  if (!t.includes("image")) return false;
  // image-to-avatar / image_to_avatar / image-to-video → these emit videos.
  if (/image[-_]to[-_](avatar|video)/.test(t)) return false;
  return true;
}

// Pull the image URL(s) out of a completion/update payload, mirroring the order
// the widget reads them. The async image jobs put the canonical URL in
// `result.url`; multi-image text-to-image jobs put an array in
// `metadata.generatedImages[].url` (and/or `result.images[].url`).
//   priority: result.images[].url → result.url → metadata.generatedImages[].url
//             → result.imageUrl → metadata.imageUrl → resultUrl
// Returns a de-duped array of non-empty string URLs (possibly empty).
function extractImageUrls(payload) {
  if (!payload || typeof payload !== "object") return [];
  const result = (payload.result && typeof payload.result === "object" && payload.result) || {};
  const metadata =
    (payload.metadata && typeof payload.metadata === "object" && payload.metadata) || {};
  const urls = [];

  const pushFromArray = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const item of arr) {
      if (typeof item === "string") urls.push(item);
      else if (item && typeof item === "object" && typeof item.url === "string")
        urls.push(item.url);
    }
  };

  pushFromArray(result.images);
  if (typeof result.url === "string") urls.push(result.url);
  pushFromArray(metadata.generatedImages);
  if (typeof result.imageUrl === "string") urls.push(result.imageUrl);
  if (typeof metadata.imageUrl === "string") urls.push(metadata.imageUrl);
  if (typeof payload.resultUrl === "string") urls.push(payload.resultUrl);

  // De-dupe, keep only non-empty trimmed strings.
  const seen = new Set();
  const out = [];
  for (const u of urls) {
    const v = typeof u === "string" ? u.trim() : "";
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

// A human-ish prompt label if the payload carries one (nice-to-have).
function extractPrompt(payload) {
  if (!payload || typeof payload !== "object") return "";
  const fromTop = typeof payload.prompt === "string" ? payload.prompt : "";
  const md = payload.metadata && typeof payload.metadata === "object" ? payload.metadata : {};
  const fromMeta = typeof md.prompt === "string" ? md.prompt : "";
  return (fromTop || fromMeta || "").trim();
}

/**
 * Subscribe to session image jobs for the current training.
 *
 * @returns {{ items: Array<{ jobId, imageUrl, prompt, status }> }}
 *   `items` is a deduped (by jobId+imageUrl) list of generated images, newest
 *   last. `status` is "generating" (seen via update) or "completed". When there
 *   is no trainingId/token, or no socket root, it stays an empty array.
 */
export default function useSessionImageJobs() {
  const trainingId = useMemo(() => readTrainingId(), []);
  const socketRoot = useMemo(() => readSocketRoot(), []);

  // Map keyed by `${jobId}::${imageUrl}` → item. One image-job can carry several
  // images; each gets its own card. Stored in a ref + mirrored to state so the
  // socket handlers can read-modify-write without stale closures.
  const itemsRef = useRef(new Map());
  const [items, setItems] = useState([]);

  const flush = () => setItems(Array.from(itemsRef.current.values()));

  // İş tepsisinde onaylanmış/reddedilmiş işler. Onaylı görsel sahneye zaten
  // eklenmiştir — kartı "Add to scene" ile yeniden sunmak ikinci kopya ekler.
  // Geç gelen socket olayları kartı geri getirmesin diye id'ler hatırlanır.
  const settledRef = useRef(new Set());
  const dropSettled = (jobIds) => {
    let removed = false;
    jobIds.forEach((id) => settledRef.current.add(id));
    itemsRef.current.forEach((item, key) => {
      if (settledRef.current.has(item.jobId)) {
        itemsRef.current.delete(key);
        removed = true;
      }
    });
    if (removed) flush();
  };
  useEffect(() => onJobsSettled(dropSettled), []);

  useEffect(() => {
    const token = getToken();
    // No-op gracefully when we can't (or shouldn't) connect.
    if (!trainingId || !socketRoot || !token) return undefined;

    const socket = io(socketRoot, {
      transports: ["websocket", "polling"],
      // Function form: re-read on every reconnect — this socket lives as long
      // as the component, well past the access token it opened with.
      auth: bearerSocketAuth(getToken),
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
    });

    const joinRoom = () => socket.emit("join_session", { trainingId });
    // Join on every (re)connect so a dropped socket re-subscribes to the room.
    socket.on("connect", joinRoom);

    const upsert = (jobId, imageUrl, prompt, status) => {
      if (!jobId || !imageUrl) return;
      const key = `${jobId}::${imageUrl}`;
      const prev = itemsRef.current.get(key);
      // Don't let a late "generating" update clobber a "completed" card.
      const nextStatus = prev && prev.status === "completed" ? "completed" : status;
      itemsRef.current.set(key, {
        jobId,
        imageUrl,
        prompt: prompt || (prev && prev.prompt) || "",
        status: nextStatus,
      });
    };

    const handle = (payload, status) => {
      if (!payload || typeof payload !== "object") return;
      const jobId = payload.jobId || payload.id;
      const jobType = payload.jobType || payload.type;
      if (!jobId || !isImageJob(jobType)) return;
      if (payload.approved === true) {
        dropSettled([jobId]);
        return;
      }
      if (settledRef.current.has(jobId)) return;
      const urls = extractImageUrls(payload);
      if (!urls.length) return;
      const prompt = extractPrompt(payload);
      let changed = false;
      const before = itemsRef.current.size;
      urls.forEach((u) => upsert(jobId, u, prompt, status));
      // Cheap change check: size grew, OR we updated an existing key's status.
      changed = itemsRef.current.size !== before || status === "completed";
      if (changed) flush();
    };

    const onUpdate = (payload) => handle(payload, "generating");
    const onCompleted = (payload) => handle(payload, "completed");
    socket.on("session_job_update", onUpdate);
    socket.on("session_job_completed", onCompleted);

    return () => {
      try {
        socket.emit("leave_session", { trainingId });
      } catch {
        // best-effort leave; disconnect below tears it down regardless.
      }
      socket.off("connect", joinRoom);
      socket.off("session_job_update", onUpdate);
      socket.off("session_job_completed", onCompleted);
      socket.disconnect();
    };
  }, [trainingId, socketRoot]);

  return { items };
}
